/**
 * TagsSearchView.jsx - Búsqueda global de tags sin importar el usuario
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import RecordCard from './RecordCard';
import EditarRegistroModal from './EditarRegistroModal';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { marcarObjetosImpresos, obtenerLotes, liberarTagsLote, venderTagsLote } from '../../../services/api';

const TagsSearchView = ({
  searchData,
  loading,
  onSearch,
  baseURL,
  addToHistory,
  initialFilter = null
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [estadoImpresionFilter, setEstadoImpresionFilter] = useState('');
  const [loteImpresionFilter, setLoteImpresionFilter] = useState('');
  const [lotes, setLotes] = useState([]);
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [linksModal, setLinksModal] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [isGeneratingQRs, setIsGeneratingQRs] = useState(false);

  // Estados para marcar como impresos
  const [showConfirmImpresion, setShowConfirmImpresion] = useState(false);
  const [nombreLote, setNombreLote] = useState('');
  const [marcandoImpresos, setMarcandoImpresos] = useState(false);

  // Estados para liberar tags
  const [showConfirmLiberar, setShowConfirmLiberar] = useState(false);
  const [liberandoTags, setLiberandoTags] = useState(false);

  // Estados para marcar tags como vendidos
  const [showConfirmVender, setShowConfirmVender] = useState(false);
  const [vendiendoTags, setVendiendoTags] = useState(false);

  // Configuración de QRs
  const [folioSize, setFolioSize] = useState('24'); // Tamaño del folio en px
  const [folioPrefix, setFolioPrefix] = useState('P'); // Prefijo del folio (P, C, U, T)

  // Estado para el modal de edición
  const [editingRecord, setEditingRecord] = useState(null);

  // ✅ FIX: Usar refs para evitar stale closures en los filtros
  const filtersRef = useRef({
    searchQuery: '',
    tipoFilter: '',
    planFilter: '',
    filterType: 'all',
    estadoImpresionFilter: '',
    loteImpresionFilter: '',
    mostrarOcultos: false
  });

  // Actualizar refs cuando cambian los estados
  useEffect(() => {
    filtersRef.current = {
      searchQuery,
      tipoFilter,
      planFilter,
      filterType,
      estadoImpresionFilter,
      loteImpresionFilter,
      mostrarOcultos
    };
  }, [searchQuery, tipoFilter, planFilter, filterType, estadoImpresionFilter, loteImpresionFilter, mostrarOcultos]);

  // Cargar lotes cuando se filtra por "impreso"
  useEffect(() => {
    const cargarLotes = async () => {
      if (estadoImpresionFilter === 'impreso') {
        const response = await obtenerLotes();
        if (response.success && response.data) {
          setLotes(response.data);
        }
      } else {
        setLotes([]);
        setLoteImpresionFilter('');
        filtersRef.current.loteImpresionFilter = '';
      }
    };
    cargarLotes();
  }, [estadoImpresionFilter]);

  // ✅ FIX: Efecto que se ejecuta CADA VEZ que cambia initialFilter
  useEffect(() => {
    if (initialFilter && initialFilter !== 'all') {
      // Resetear TODO el estado
      setCurrentPage(1);
      setFilterType(initialFilter);
      setSearchQuery('');
      setTipoFilter('');
      setHasSearched(true);
      
      // Construir filtros correctos
      const filters = {
        tipo: '',
        estado: initialFilter,
        metricFilter: initialFilter // ✅ Pasar también como metricFilter para casos especiales
      };

      // Ejecutar búsqueda con página 1
      onSearch('', filters, 1, 20);
      addToHistory(`Filtro automático aplicado: ${initialFilter}`);
    }
  }, [initialFilter, onSearch, addToHistory]);

  // ✅ FIX: handleSearch que usa refs para evitar stale closures
  const handleSearch = useCallback(async (e, resetPage = false) => {
    if (e) e.preventDefault();

    const pageToUse = resetPage ? 1 : currentPage;

    if (resetPage) {
      setCurrentPage(1);
    }

    // ✅ IMPORTANTE: Leer valores desde el ref para evitar stale closures
    const currentFilters = filtersRef.current;

    // ✅ Construir filtros con valores actuales del ref
    const filters = {
      tipo: currentFilters.tipoFilter || '',
      estado: currentFilters.filterType === 'all' ? '' : currentFilters.filterType,
      plan: currentFilters.planFilter || '',
      estadoImpresion: currentFilters.estadoImpresionFilter || '',
      loteImpresion: currentFilters.loteImpresionFilter || '',
      incluirOcultos: currentFilters.mostrarOcultos || false
    };

    // Si el estado viene de una métrica especial, agregarlo
    if (currentFilters.filterType !== 'all') {
      filters.metricFilter = currentFilters.filterType;
    }

    await onSearch(currentFilters.searchQuery, filters, pageToUse, 20);
    setHasSearched(true);
    addToHistory(`Búsqueda: "${currentFilters.searchQuery || 'todos'}" - Pág ${pageToUse}`);
  }, [currentPage, onSearch, addToHistory]);

  // ✅ FIX: Limpiar filtros completamente
  const handleClearFilters = () => {
    // Limpiar estados
    setFilterType('all');
    setSearchQuery('');
    setTipoFilter('');
    setPlanFilter('');
    setEstadoImpresionFilter('');
    setLoteImpresionFilter('');
    setLotes([]);
    setMostrarOcultos(false);
    setCurrentPage(1);
    setHasSearched(false);

    // ✅ También limpiar el ref inmediatamente
    filtersRef.current = {
      searchQuery: '',
      tipoFilter: '',
      planFilter: '',
      filterType: 'all',
      estadoImpresionFilter: '',
      loteImpresionFilter: '',
      mostrarOcultos: false
    };

    // Buscar TODO sin filtros
    onSearch('', { tipo: '', estado: '', plan: '', estadoImpresion: '', loteImpresion: '', metricFilter: null, incluirOcultos: false }, 1, 20);
    addToHistory('Filtros limpiados');
  };

  // ✅ FIX: Cambio de página que usa refs para mantener filtros actuales
  const handlePageChange = (newPage) => {
    if (newPage < 1) return;

    setCurrentPage(newPage);

    // ✅ Usar ref para obtener valores actuales
    const currentFilters = filtersRef.current;

    const filters = {
      tipo: currentFilters.tipoFilter || '',
      estado: currentFilters.filterType === 'all' ? '' : currentFilters.filterType,
      plan: currentFilters.planFilter || '',
      estadoImpresion: currentFilters.estadoImpresionFilter || '',
      loteImpresion: currentFilters.loteImpresionFilter || '',
      incluirOcultos: currentFilters.mostrarOcultos || false
    };

    if (currentFilters.filterType !== 'all') {
      filters.metricFilter = currentFilters.filterType;
    }

    onSearch(currentFilters.searchQuery, filters, newPage, 20);
    addToHistory(`Página ${newPage}`);
  };

  const handleGenerateLinks = (record) => {
    const searchUrl = `${baseURL}/consultar?registro=${record.id}`;
    const activateUrl = `${baseURL}/activar?activacion=${record.codigoAcceso}`;

    setLinksModal({
      searchUrl,
      activateUrl,
      recordName: record.nombre || 'Registro',
      recordId: record.id,
      folio: record.folio,
      tipo: record.tipo
    });

    addToHistory(`Enlaces: ${record.nombre || record.id}`);
  };

  // Manejar selección de registros
  const handleToggleSelect = useCallback((recordId) => {
    setSelectedRecords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  }, []);

  // Seleccionar todos los visibles
  const handleSelectAll = useCallback(() => {
    if (searchData?.records) {
      const allIds = new Set(searchData.records.map(r => r.id));
      setSelectedRecords(allIds);
      addToHistory(`Seleccionados ${allIds.size} tags`);
    }
  }, [searchData, addToHistory]);

  // Deseleccionar todos
  const handleDeselectAll = useCallback(() => {
    setSelectedRecords(new Set());
    addToHistory('Deseleccionados todos los tags');
  }, [addToHistory]);

  // Verificar si todos los seleccionados están impresos (para habilitar Liberar)
  const todosImpresos = React.useMemo(() => {
    if (selectedRecords.size === 0 || !searchData?.records) return false;
    const selectedIds = Array.from(selectedRecords);
    return selectedIds.every(id => {
      const record = searchData.records.find(r => r.id === id);
      return record && record.estadoImpresion === 'impreso';
    });
  }, [selectedRecords, searchData?.records]);

  // Verificar si todos los seleccionados están liberados (para habilitar Vendido)
  const todosLiberados = React.useMemo(() => {
    if (selectedRecords.size === 0 || !searchData?.records) return false;
    const selectedIds = Array.from(selectedRecords);
    return selectedIds.every(id => {
      const record = searchData.records.find(r => r.id === id);
      return record && record.liberado === true;
    });
  }, [selectedRecords, searchData?.records]);

  // Contar cuántos no están impresos y cuántos no están liberados
  const { noImpresos, noLiberados } = React.useMemo(() => {
    if (selectedRecords.size === 0 || !searchData?.records) {
      return { noImpresos: 0, noLiberados: 0 };
    }
    const selectedIds = Array.from(selectedRecords);
    let countNoImpresos = 0;
    let countNoLiberados = 0;

    selectedIds.forEach(id => {
      const record = searchData.records.find(r => r.id === id);
      if (record) {
        if (record.estadoImpresion !== 'impreso') countNoImpresos++;
        if (record.liberado !== true) countNoLiberados++;
      }
    });

    return { noImpresos: countNoImpresos, noLiberados: countNoLiberados };
  }, [selectedRecords, searchData?.records]);

  // Marcar seleccionados como impresos
  const handleMarcarImpresos = async () => {
    if (selectedRecords.size === 0) return;

    setMarcandoImpresos(true);
    try {
      const idsArray = Array.from(selectedRecords);
      const resultado = await marcarObjetosImpresos(
        idsArray,
        nombreLote || null
      );

      if (resultado.error) {
        alert(`Error: ${resultado.error}`);
        return;
      }

      alert(`✅ ${resultado.marcados} tags marcados como impresos (Lote: ${resultado.lote})`);
      addToHistory(`Marcados ${resultado.marcados} tags como impresos - Lote: ${resultado.lote}`);
      setSelectedRecords(new Set());
      setShowConfirmImpresion(false);
      setNombreLote('');
      // Refrescar la búsqueda
      handleSearch(null, false);
    } catch (err) {
      console.error('Error al marcar impresos:', err);
      alert('Error al marcar como impresos');
    } finally {
      setMarcandoImpresos(false);
    }
  };

  // Liberar tags seleccionados para venta
  const handleLiberarTags = async () => {
    if (selectedRecords.size === 0) return;

    setLiberandoTags(true);
    try {
      const idsArray = Array.from(selectedRecords);
      const resultado = await liberarTagsLote(idsArray);

      if (resultado.error) {
        alert(`Error: ${resultado.error}`);
        return;
      }

      const mensajeExito = resultado.errores && resultado.errores.length > 0
        ? `${resultado.liberados} tags liberados. ${resultado.errores.length} errores.`
        : `${resultado.liberados} tags liberados correctamente`;

      alert(mensajeExito);
      addToHistory(`Liberados ${resultado.liberados} tags para venta`);
      setSelectedRecords(new Set());
      setShowConfirmLiberar(false);
      // Refrescar la búsqueda
      handleSearch(null, false);
    } catch (err) {
      console.error('Error al liberar tags:', err);
      alert('Error al liberar tags');
    } finally {
      setLiberandoTags(false);
    }
  };

  // Marcar tags como vendidos (salieron de inventario)
  const handleVenderTags = async () => {
    if (selectedRecords.size === 0) return;

    setVendiendoTags(true);
    try {
      const idsArray = Array.from(selectedRecords);
      const resultado = await venderTagsLote(idsArray);

      if (resultado.error) {
        alert(`Error: ${resultado.error}`);
        return;
      }

      const mensajeExito = resultado.errores && resultado.errores.length > 0
        ? `${resultado.vendidos} tags marcados como vendidos. ${resultado.errores.length} errores.`
        : `${resultado.vendidos} tags marcados como vendidos correctamente`;

      alert(mensajeExito);
      addToHistory(`Marcados ${resultado.vendidos} tags como vendidos`);
      setSelectedRecords(new Set());
      setShowConfirmVender(false);
      // Refrescar la búsqueda
      handleSearch(null, false);
    } catch (err) {
      console.error('Error al marcar tags como vendidos:', err);
      alert('Error al marcar tags como vendidos');
    } finally {
      setVendiendoTags(false);
    }
  };

  // Generar QR como imagen PNG con folio (tamaño configurable)
  const generateQRImage = async (url, folio, prefix, size, folioFontSize = 24) => {
    // Generar QR como data URL
    const qrDataUrl = await QRCode.toDataURL(url, {
      width: size,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' }
    });

    // Calcular altura del área de folio basada en el tamaño de fuente
    const folioHeight = Math.max(30, parseInt(folioFontSize) + 16);

    // Crear canvas con espacio para el folio
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = size;
    canvas.height = size + folioHeight;

    // Fondo blanco
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Cargar y dibujar el QR
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, size, size);

        // Formatear folio con 6 dígitos
        const folioFormateado = folio.toString().padStart(6, '0');
        const folioCompleto = `${prefix}${folioFormateado}`;

        // Dibujar folio debajo del QR con tamaño configurable
        ctx.fillStyle = 'black';
        ctx.font = `bold ${folioFontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(folioCompleto, size / 2, size + (folioHeight / 2));

        // Convertir a blob
        canvas.toBlob((blob) => {
          resolve({ blob, filename: `${folioCompleto}.png` });
        }, 'image/png');
      };
      img.onerror = reject;
      img.src = qrDataUrl;
    });
  };

  // Generar QR como SVG con folio (ideal para grabado láser)
  const generateQRSvg = async (url, folio, prefix, size, folioFontSize = 24) => {
    // Generar QR como string SVG
    const qrSvgString = await QRCode.toString(url, {
      type: 'svg',
      width: size,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' }
    });

    // Formatear folio con 6 dígitos
    const folioFormateado = folio.toString().padStart(6, '0');
    const folioCompleto = `${prefix}${folioFormateado}`;

    // Extraer el viewBox original del SVG generado
    const viewBoxMatch = qrSvgString.match(/viewBox="([^"]+)"/);
    const originalViewBox = viewBoxMatch ? viewBoxMatch[1] : `0 0 ${size} ${size}`;

    // Extraer el contenido interno del SVG (los paths)
    const contentMatch = qrSvgString.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
    const innerContent = contentMatch ? contentMatch[1] : '';

    // Calcular dimensiones del SVG final
    const padding = 10;
    const textAreaHeight = parseInt(folioFontSize) + padding * 2;
    const totalHeight = size + textAreaHeight;

    // Crear SVG completo con QR + folio
    const fullSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${totalHeight}">
  <rect width="100%" height="100%" fill="white"/>
  <svg x="0" y="0" width="${size}" height="${size}" viewBox="${originalViewBox}">
    ${innerContent}
  </svg>
  <text
    x="${size / 2}"
    y="${size + padding + parseInt(folioFontSize) * 0.8}"
    text-anchor="middle"
    font-family="Arial, Helvetica, sans-serif"
    font-size="${folioFontSize}"
    font-weight="bold"
    fill="black"
  >${folioCompleto}</text>
</svg>`;

    // Convertir a Blob
    const blob = new Blob([fullSvg], { type: 'image/svg+xml' });
    return { blob, filename: `${folioCompleto}.svg` };
  };

  // Descargar QRs en lote con prefijo y formato configurables
  const handleDownloadQRs = async (formato) => {
    if (selectedRecords.size === 0) return;

    setIsGeneratingQRs(true);
    const formatoLabel = formato.toUpperCase();
    addToHistory(`Generando ${selectedRecords.size} QRs ${formatoLabel} (${folioPrefix})...`);

    try {
      const zip = new JSZip();
      const selectedRecordsList = searchData.records.filter(r => selectedRecords.has(r.id));

      // Función generadora según formato
      const generateQR = formato === 'svg' ? generateQRSvg : generateQRImage;
      const folioFontSize = parseInt(folioSize);

      // Tamaño del QR: 2cm = 236px a 300 DPI
      const qrSize = 236;

      for (const record of selectedRecordsList) {
        const folio = record.folio;
        // URL de consulta pública - es la que usa quien encuentra el objeto
        const url = `${baseURL}/consultar?registro=${record.id}`;
        const qrResult = await generateQR(url, folio, folioPrefix, qrSize, folioFontSize);
        zip.file(qrResult.filename, qrResult.blob);
      }

      // Generar y descargar ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().slice(0, 10);
      saveAs(zipBlob, `QRs_${folioPrefix}_${formatoLabel}_${selectedRecords.size}_tags_${timestamp}.zip`);

      addToHistory(`Descargados ${selectedRecords.size} QRs ${formatoLabel} (${folioPrefix})`);
    } catch (error) {
      console.error('Error generando QRs:', error);
      addToHistory('Error al generar QRs');
    } finally {
      setIsGeneratingQRs(false);
    }
  };

  return (
    <div className="tags-search-view">
      {/* ✅ Banner de filtro activo mejorado */}
      {initialFilter && initialFilter !== 'all' && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
          <div>
            <i className="bi bi-funnel-fill me-2"></i>
            Filtro activo: <strong className="text-capitalize">{initialFilter}</strong>
            <br/>
            <small className="text-muted">
              Mostrando solo registros con estado: {initialFilter}
            </small>
          </div>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={handleClearFilters}
          >
            <i className="bi bi-x-circle me-1"></i>
            Limpiar
          </button>
        </div>
      )}

      {/* Formulario de búsqueda */}
      <form onSubmit={(e) => handleSearch(e, true)} className="mb-4">
        <div className="row g-2">
          <div className="col-12 col-md-4">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por folio, código o email..."
                value={searchQuery}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchQuery(val);
                  filtersRef.current.searchQuery = val;
                }}
              />
            </div>
          </div>

          <div className="col-6 col-md-2">
            <select
              className="form-select"
              value={tipoFilter}
              onChange={(e) => {
                const val = e.target.value;
                setTipoFilter(val);
                filtersRef.current.tipoFilter = val;
                setCurrentPage(1);
              }}
            >
              <option value="">Todos los tipos</option>
              <option value="mascota">🐾 Mascotas</option>
              <option value="llavero">🔑 Llaveros</option>
              <option value="coleccionable">🏆 Coleccionables</option>
            </select>
          </div>

          <div className="col-6 col-md-2">
            <select
              className="form-select"
              value={planFilter}
              onChange={(e) => {
                const val = e.target.value;
                setPlanFilter(val);
                filtersRef.current.planFilter = val;
                setCurrentPage(1);
              }}
            >
              <option value="">Todos los planes</option>
              <option value="light">Light</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          {/* Filtro de estado de impresión */}
          <div className="col-6 col-md-2">
            <select
              className="form-select"
              value={estadoImpresionFilter}
              onChange={(e) => {
                const val = e.target.value;
                setEstadoImpresionFilter(val);
                filtersRef.current.estadoImpresionFilter = val;
                setCurrentPage(1);
              }}
            >
              <option value="">Impresión: Todos</option>
              <option value="pendiente">🟡 Pendiente</option>
              <option value="impreso">✅ Impreso</option>
            </select>
          </div>

          {/* Filtro de lote de impresión (solo visible cuando filtro = impreso) */}
          {estadoImpresionFilter === 'impreso' && lotes.length > 0 && (
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={loteImpresionFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setLoteImpresionFilter(val);
                  filtersRef.current.loteImpresionFilter = val;
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos los lotes</option>
                {lotes.map(l => (
                  <option key={l.lote} value={l.lote}>
                    📦 {l.lote} ({l.cantidad})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-6 col-md-2">
            <select
              className="form-select"
              value={filterType}
              onChange={(e) => {
                const val = e.target.value;
                setFilterType(val);
                filtersRef.current.filterType = val;
                setCurrentPage(1);
              }}
            >
              <option value="all">Todos los estados</option>
              <option value="activo">✅ Activos</option>
              <option value="inactivo">⏳ Inactivos</option>
              <option value="pendientePorLigar">🔗 Por ligar</option>
              <option value="pendienteVerificacion">📧 Pendiente verificación</option>
              <option value="perdidos">🚨 Perdidos</option>
            </select>
          </div>

          {/* Checkbox Mostrar ocultos */}
          <div className="col-auto d-flex align-items-center">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="mostrarOcultos"
                checked={mostrarOcultos}
                onChange={(e) => {
                  const val = e.target.checked;
                  setMostrarOcultos(val);
                  filtersRef.current.mostrarOcultos = val;
                }}
              />
              <label className="form-check-label small" htmlFor="mostrarOcultos">
                <i className="bi bi-eye-slash me-1"></i>Ocultos
              </label>
            </div>
          </div>

          <div className="col-6 col-md-2">
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  ...
                </>
              ) : (
                <>
                  <i className="bi bi-search me-1"></i>
                  Buscar
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Resultados */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Buscando tags...</p>
        </div>
      ) : searchData?.records?.length > 0 ? (
        <>
          {/* Info de resultados */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div className="text-muted">
                Mostrando <strong>{searchData.records.length}</strong> de <strong>{searchData.totalRecords}</strong> registros
              </div>
              <small className="text-muted">
                Página <strong>{searchData.currentPage || currentPage}</strong> de <strong>{searchData.totalPages}</strong>
              </small>
            </div>
            {(filterType !== 'all' || tipoFilter || planFilter || estadoImpresionFilter || loteImpresionFilter || searchQuery) && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleClearFilters}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Limpiar
              </button>
            )}
          </div>

          {/* Barra de selección y exportación integrada */}
          <div className="bg-light border rounded p-2 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              {/* Botones de selección */}
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleSelectAll}
                disabled={isGeneratingQRs}
              >
                <i className="bi bi-check-all me-1"></i>
                Seleccionar todos
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleDeselectAll}
                disabled={isGeneratingQRs || selectedRecords.size === 0}
              >
                <i className="bi bi-x-lg me-1"></i>
                Deseleccionar
              </button>

              {/* Contador de seleccionados */}
              {selectedRecords.size > 0 && (
                <>
                  <span className="text-muted mx-2">|</span>
                  <span className="fw-bold text-primary">
                    <i className="bi bi-check2-square me-1"></i>
                    {selectedRecords.size} seleccionado{selectedRecords.size !== 1 ? 's' : ''}
                  </span>

                  {/* Configuración de QRs */}
                  <span className="text-muted mx-2">|</span>

                  {/* Prefijo del folio */}
                  <div className="d-flex align-items-center gap-1">
                    <label className="small text-muted mb-0" style={{ whiteSpace: 'nowrap' }}>
                      Prefijo:
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={folioPrefix}
                      onChange={(e) => setFolioPrefix(e.target.value)}
                      disabled={isGeneratingQRs}
                      style={{ width: 'auto', minWidth: '80px' }}
                    >
                      <option value="P">P (Producción)</option>
                      <option value="U">U (Pruebas)</option>
                    </select>
                  </div>

                  {/* Tamaño del folio */}
                  <div className="d-flex align-items-center gap-1">
                    <label className="small text-muted mb-0" style={{ whiteSpace: 'nowrap' }}>
                      <i className="bi bi-fonts me-1"></i>Tamaño:
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={folioSize}
                      onChange={(e) => setFolioSize(e.target.value)}
                      disabled={isGeneratingQRs}
                      style={{ width: 'auto', minWidth: '130px' }}
                    >
                      <option value="14">Pequeño (14px)</option>
                      <option value="20">Mediano (20px)</option>
                      <option value="24">Grande (24px)</option>
                      <option value="28">Muy grande (28px)</option>
                      <option value="36">Extra grande (36px)</option>
                    </select>
                  </div>

                  {/* Botones de descarga */}
                  <span className="text-muted mx-2">|</span>
                  <div className="btn-group">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleDownloadQRs('png')}
                      disabled={isGeneratingQRs}
                      title="Descargar QRs en PNG"
                    >
                      {isGeneratingQRs ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <>
                          <i className="bi bi-image me-1"></i>
                          PNG
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleDownloadQRs('svg')}
                      disabled={isGeneratingQRs}
                      title="Descargar QRs en SVG (ideal para láser)"
                    >
                      {isGeneratingQRs ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <>
                          <i className="bi bi-filetype-svg me-1"></i>
                          SVG
                        </>
                      )}
                    </button>
                  </div>

                  {/* Botón Marcar como impresos */}
                  <span className="text-muted mx-2">|</span>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => setShowConfirmImpresion(true)}
                    disabled={isGeneratingQRs || marcandoImpresos}
                    title="Marcar tags seleccionados como impresos"
                  >
                    <i className="bi bi-printer me-1"></i>
                    Marcar impresos
                  </button>

                  {/* Botón Liberar tags para venta */}
                  <button
                    className={`btn btn-sm ms-2 ${todosImpresos ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowConfirmLiberar(true)}
                    disabled={isGeneratingQRs || liberandoTags || !todosImpresos}
                    title={
                      !todosImpresos
                        ? `${noImpresos} tag(s) no están impresos. Deben estar impresos para liberarlos.`
                        : 'Liberar tags seleccionados para venta/activación'
                    }
                  >
                    <i className="bi bi-unlock me-1"></i>
                    Liberar
                    {!todosImpresos && noImpresos > 0 && (
                      <span className="badge bg-warning text-dark ms-1" title="Tags sin imprimir">
                        {noImpresos}
                      </span>
                    )}
                  </button>

                  {/* Botón Marcar como vendido */}
                  <button
                    className={`btn btn-sm ms-2 ${todosLiberados ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setShowConfirmVender(true)}
                    disabled={isGeneratingQRs || vendiendoTags || !todosLiberados}
                    title={
                      !todosLiberados
                        ? `${noLiberados} tag(s) no están liberados. Deben estar liberados para marcarlos como vendidos.`
                        : 'Marcar tags como vendidos (salieron de inventario)'
                    }
                  >
                    <i className="bi bi-truck me-1"></i>
                    Vendido
                    {!todosLiberados && noLiberados > 0 && (
                      <span className="badge bg-warning text-dark ms-1" title="Tags sin liberar">
                        {noLiberados}
                      </span>
                    )}
                  </button>
                </>
              )}
            </div>
            {/* Tip sobre SVG */}
            {selectedRecords.size > 0 && (
              <div className="mt-2 small text-muted">
                <i className="bi bi-lightbulb me-1"></i>
                SVG recomendado para grabado láser (vectorial, escalable sin pérdida)
              </div>
            )}
          </div>

          {/* Lista de registros */}
          <div className="records-list">
            {searchData.records.map(record => (
              <RecordCard
                key={record.id}
                record={record}
                selected={selectedRecords.has(record.id)}
                onToggleSelect={() => handleToggleSelect(record.id)}
                onGenerateLinks={handleGenerateLinks}
                onEdit={(rec) => {
                  setEditingRecord(rec);
                }}
                baseURL={baseURL}
                compact={true}
                loading={false}
              />
            ))}
          </div>

          {/* Paginación */}
          {searchData.totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center flex-wrap">
                {/* Botón Anterior */}
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    <i className="bi bi-chevron-left"></i> Anterior
                  </button>
                </li>
                
                {/* Páginas */}
                {(() => {
                  const pages = [];
                  const totalPages = searchData.totalPages;
                  const current = searchData.currentPage || currentPage;
                  
                  // Calcular rango de páginas a mostrar
                  let startPage = Math.max(1, current - 2);
                  let endPage = Math.min(totalPages, current + 2);
                  
                  // Ajustar si estamos cerca del inicio o fin
                  if (current <= 3) {
                    endPage = Math.min(5, totalPages);
                  }
                  if (current >= totalPages - 2) {
                    startPage = Math.max(1, totalPages - 4);
                  }
                  
                  // Primera página si no está en el rango
                  if (startPage > 1) {
                    pages.push(
                      <li key={1} className="page-item">
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(1)} 
                          disabled={loading}
                        >
                          1
                        </button>
                      </li>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <li key="dots1" className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                  }
                  
                  // Páginas del rango
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <li key={i} className={`page-item ${current === i ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(i)} 
                          disabled={loading}
                        >
                          {i}
                        </button>
                      </li>
                    );
                  }
                  
                  // Última página si no está en el rango
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <li key="dots2" className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    pages.push(
                      <li key={totalPages} className="page-item">
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(totalPages)} 
                          disabled={loading}
                        >
                          {totalPages}
                        </button>
                      </li>
                    );
                  }
                  
                  return pages;
                })()}
                
                {/* Botón Siguiente */}
                <li className={`page-item ${currentPage === searchData.totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === searchData.totalPages || loading}
                  >
                    Siguiente <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
              
              {/* Info adicional */}
              <div className="text-center mt-2">
                <small className="text-muted">
                  Página {searchData.currentPage || currentPage} de {searchData.totalPages} • {searchData.totalRecords} resultados
                </small>
              </div>
            </nav>
          )}
        </>
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox" style={{fontSize: '3rem'}}></i>
          <p className="mt-3 mb-3">No se encontraron resultados</p>
          {(filterType !== 'all' || tipoFilter || estadoImpresionFilter || loteImpresionFilter || searchQuery) && (
            <>
              <p className="small text-muted mb-3">
                Filtros activos:
                {filterType !== 'all' && <span className="badge bg-secondary ms-2">{filterType}</span>}
                {tipoFilter && <span className="badge bg-secondary ms-2">{tipoFilter}</span>}
                {estadoImpresionFilter && <span className="badge bg-secondary ms-2">Impresión: {estadoImpresionFilter}</span>}
                {loteImpresionFilter && <span className="badge bg-info ms-2">📦 {loteImpresionFilter}</span>}
                {searchQuery && <span className="badge bg-secondary ms-2">"{searchQuery}"</span>}
              </p>
              <button
                className="btn btn-outline-primary"
                onClick={handleClearFilters}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Limpiar filtros
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-search" style={{fontSize: '3rem'}}></i>
          <p className="mt-3">Usa los filtros o haz clic en las métricas</p>
          <small className="text-muted">
            Las métricas del panel superior aplicarán filtros automáticamente
          </small>
        </div>
      )}

      {/* Modal de enlaces (sin cambios) */}
      {linksModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-link-45deg me-2"></i>
                  Enlaces de {linksModal.recordName}
                </h5>
                <button type="button" className="btn-close" onClick={() => setLinksModal(null)} />
              </div>
              <div className="modal-body">
                <div className="alert alert-info small mb-3">
                  <strong>Registro:</strong> {linksModal.recordName}<br/>
                  <strong>ID:</strong> {linksModal.recordId}<br/>
                  <strong>Folio:</strong> {linksModal.folio}<br/>
                  {linksModal.tipo && <><strong>Tipo:</strong> {linksModal.tipo}</>}
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    <i className="bi bi-search me-2"></i>
                    Enlace de Búsqueda:
                  </label>
                  <div className="input-group">
                    <input type="text" className="form-control" value={linksModal.searchUrl} readOnly />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(linksModal.searchUrl);
                        const btn = document.activeElement;
                        const originalText = btn.innerHTML;
                        btn.innerHTML = '<i class="bi bi-check"></i> Copiado';
                        setTimeout(() => { if (btn) btn.innerHTML = originalText; }, 2000);
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copiar
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label fw-bold">
                    <i className="bi bi-key me-2"></i>
                    Enlace de Activación:
                  </label>
                  <div className="input-group">
                    <input type="text" className="form-control" value={linksModal.activateUrl} readOnly />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(linksModal.activateUrl);
                        const btn = document.activeElement;
                        const originalText = btn.innerHTML;
                        btn.innerHTML = '<i class="bi bi-check"></i> Copiado';
                        setTimeout(() => { if (btn) btn.innerHTML = originalText; }, 2000);
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copiar
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setLinksModal(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de registro */}
      <EditarRegistroModal
        show={editingRecord !== null}
        onClose={() => setEditingRecord(null)}
        objetoId={editingRecord?.id}
        onSuccess={(updatedRecord) => {
          setEditingRecord(null);
          // Refrescar la búsqueda para mostrar los cambios
          handleSearch(null, false);
          addToHistory(`Registro actualizado: ${updatedRecord?.nombre || updatedRecord?.id || 'Eliminado'}`);
        }}
      />

      {/* Modal de confirmación de impresión */}
      {showConfirmImpresion && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-printer me-2"></i>
                  Confirmar impresión
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowConfirmImpresion(false);
                    setNombreLote('');
                  }}
                  disabled={marcandoImpresos}
                />
              </div>
              <div className="modal-body">
                <p>¿Marcar <strong>{selectedRecords.size}</strong> tag(s) como impresos?</p>
                <div className="mb-3">
                  <label className="form-label">Nombre del lote (opcional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`LOTE-${new Date().toISOString().slice(0, 10)}`}
                    value={nombreLote}
                    onChange={(e) => setNombreLote(e.target.value)}
                    disabled={marcandoImpresos}
                  />
                  <small className="text-muted">
                    Si no escribes un nombre, se usará: LOTE-{new Date().toISOString().slice(0, 10)}
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowConfirmImpresion(false);
                    setNombreLote('');
                  }}
                  disabled={marcandoImpresos}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleMarcarImpresos}
                  disabled={marcandoImpresos}
                >
                  {marcandoImpresos ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Marcando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check2 me-1"></i>
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de liberación */}
      {showConfirmLiberar && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-unlock me-2 text-success"></i>
                  Liberar Tags para Venta
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmLiberar(false)}
                  disabled={liberandoTags}
                />
              </div>
              <div className="modal-body">
                <p>¿Liberar <strong>{selectedRecords.size}</strong> tag(s) para venta/activación?</p>
                <div className="alert alert-info small mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Una vez liberados:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Los tags podrán ser activados por los clientes</li>
                    <li>Se registrará la fecha de liberación</li>
                    <li>Aparecerán como "Listos para Enviar" en MKT Panel</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmLiberar(false)}
                  disabled={liberandoTags}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleLiberarTags}
                  disabled={liberandoTags}
                >
                  {liberandoTags ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Liberando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-unlock me-1"></i>
                      Liberar Tags
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de venta */}
      {showConfirmVender && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-truck me-2 text-primary"></i>
                  Marcar Tags como Vendidos
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmVender(false)}
                  disabled={vendiendoTags}
                />
              </div>
              <div className="modal-body">
                <p>¿Marcar <strong>{selectedRecords.size}</strong> tag(s) como vendidos?</p>
                <div className="alert alert-warning small mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Esto indica que:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Los tags han salido del inventario físico</li>
                    <li>Fueron entregados al cliente o distribuidor</li>
                    <li>Ya no cuentan en el inventario disponible</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmVender(false)}
                  disabled={vendiendoTags}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleVenderTags}
                  disabled={vendiendoTags}
                >
                  {vendiendoTags ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Marcando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-truck me-1"></i>
                      Marcar Vendidos
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsSearchView;